'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import PageBackdrop from '@/components/PageBackdrop';

interface Release {
  id: number;
  title: string;
  artist: string;
  type: 'Album' | 'EP' | 'Single' | 'Video';
  year: string;
  image: string;
  alt: string;
  role: string;
  genre: string;
  listenUrl: string;
  buyUrl?: string;
  tracks?: number;
}

const releases: Release[] = [
  {
    id: 5,
    title: 'MURDER8 Drum Cover',
    artist: 'Bodysnatcher',
    type: 'Video',
    year: '2026',
    image: '/assets/images/murder8-cover-thumb.jpg',
    alt: 'Diggity Glover — MURDER8 by Bodysnatcher full drum cover',
    role: 'Full Drum Cover',
    genre: 'Deathcore',
    listenUrl: 'https://www.youtube.com/watch?v=2pkBXutnSAA',
  },
  {
    id: 6,
    title: 'Death Will Follow Me Drum Cover',
    artist: 'Boundaries',
    type: 'Video',
    year: '2026',
    image: '/assets/images/boundaries-cover-thumb.jpg',
    alt: 'Diggity Glover — Death Will Follow Me by Boundaries full drum cover',
    role: 'Full Drum Cover',
    genre: 'Metalcore, Hardcore',
    listenUrl: 'https://www.youtube.com/watch?v=XP_srzUoBz0',
  },
  {
    id: 1,
    title: '(Not in your) Nature',
    artist: 'Covert UK',
    type: 'EP',
    year: '2025',
    image: '/assets/images/9-1773171512877.png',
    alt: 'Covert UK — (Not in your) Nature artwork, dark metalcore aesthetic',
    role: 'Mix & Master',
    genre: 'Nu Metal, Metalcore',
    listenUrl: 'https://www.youtube.com/watch?v=x_dY3-tt_-M',
  },
  {
    id: 2,
    title: 'Twisted Mind',
    artist: 'Covert UK',
    type: 'Single',
    year: '2025',
    image: '/assets/images/10-1773171732469.png',
    alt: 'Covert UK — Twisted Mind single artwork, aggressive metalcore design',
    role: 'Recording + Mix',
    genre: 'Metalcore, Hardcore',
    listenUrl: 'https://www.youtube.com/watch?v=ozgr6Tbhzvw',
  },
  {
    id: 3,
    title: 'Altar',
    artist: 'Covert UK',
    type: 'Single',
    year: '2025',
    image: '/assets/images/Screenshot_2026-03-10_at_19.35.22-1773171951618.png',
    alt: 'Covert UK — Altar artwork, metalcore',
    role: 'Recording + Mix',
    genre: 'Metalcore, Hardcore',
    listenUrl: 'https://www.youtube.com/watch?v=2l6OQFlcLWw',
  },
  {
    id: 4,
    title: 'Nothing More Drum Playthrough',
    artist: 'Dystopian Sun',
    type: 'Single',
    year: '2025',
    image: '/assets/images/Untitled_design-1773172263226.png',
    alt: 'Dystopian Sun — Nothing More drum playthrough artwork, deathcore',
    role: 'Session Drums',
    genre: 'Deathcore',
    listenUrl: 'https://www.youtube.com/watch?v=3nU6wFoxTxo',
  },
];

export default function MusicPage() {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div
      style={{ minHeight: '100vh', background: 'transparent', color: '#ffffff', overflowX: 'hidden' }}
    >
      <PageBackdrop src="/assets/images/diggity-10.jpg" />
      <Header />

      {/* ─── PAGE HEADER ─── */}
      <section style={{ padding: '60px 24px 48px', borderBottom: '1px solid #ff1a1a' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <p
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: '#ff1a1a',
              marginBottom: '16px',
            }}
          >
            // Release Catalogue
          </p>
          <h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 900,
              textTransform: 'uppercase',
              fontSize: 'clamp(2.5rem, 7vw, 5rem)',
              lineHeight: '0.95',
              color: '#ffffff',
              textShadow: '0 0 20px rgba(255,26,26,0.7)',
              marginBottom: '16px',
            }}
          >
            Music
          </h1>
          <p
            style={{
              fontFamily: 'Manrope, sans-serif',
              color: '#cccccc',
              fontSize: '1.05rem',
              maxWidth: '500px',
              lineHeight: '1.7',
            }}
          >
            Albums, EPs, and singles. Production credits, session work, and original releases.
          </p>
        </div>
      </section>

      {/* ─── RELEASE GRID ─── */}
      <section style={{ padding: '60px 24px 80px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          {/* Featured release */}
          {releases[0] && (
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  border: '1px solid #ff1a1a',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  cursor: 'pointer',
                  transition: 'box-shadow 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px rgba(255,26,26,0.3)';
                  setHovered(releases[0].id);
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  setHovered(null);
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    aspectRatio: '1/1',
                    overflow: 'hidden',
                    background: '#000000',
                    minHeight: '280px',
                  }}
                >
                  <AppImage
                    src={releases[0].image}
                    alt={releases[0].alt}
                    width={800}
                    height={800}
                    className="w-full h-full object-cover"
                    style={{
                      filter:
                        hovered === releases[0].id
                          ? 'grayscale(0%) contrast(1.05)'
                          : 'grayscale(60%) contrast(1.1)',
                      transition: 'filter 0.5s ease, transform 0.5s ease',
                      transform: hovered === releases[0].id ? 'scale(1.04)' : 'scale(1)',
                    }}
                  />
                </div>
                <div
                  style={{
                    background: '#1a1a1a',
                    padding: '32px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div
                      style={{
                        background: '#ff1a1a',
                        color: '#000000',
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        padding: '5px 10px',
                        display: 'inline-block',
                        marginBottom: '20px',
                      }}
                    >
                      {releases[0].type} · {releases[0].year}
                    </div>
                    <h2
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 900,
                        textTransform: 'uppercase',
                        fontSize: 'clamp(1.8rem, 4vw, 3rem)',
                        lineHeight: '1',
                        color: '#ffffff',
                        textShadow: '0 0 10px rgba(255,26,26,0.5)',
                        marginBottom: '8px',
                      }}
                    >
                      {releases[0].title}
                    </h2>
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.8rem',
                        color: '#cccccc',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        marginBottom: '20px',
                      }}
                    >
                      {releases[0].artist}
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        marginBottom: '28px',
                      }}
                    >
                      <p
                        style={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.7rem',
                          color: '#666666',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {releases[0].genre}
                      </p>
                      <p
                        style={{
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.7rem',
                          color: '#ff1a1a',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {releases[0].role}
                      </p>
                      {releases[0].tracks && (
                        <p
                          style={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.7rem',
                            color: '#666666',
                          }}
                        >
                          {releases[0].tracks} tracks
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <a
                      href={releases[0].listenUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-primary"
                      style={{ fontSize: '0.8rem', padding: '10px 24px' }}
                    >
                      {releases[0].type === 'Video' ? 'Watch' : 'Listen'}
                    </a>
                    {releases[0].buyUrl && (
                      <a
                        href={releases[0].buyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline"
                        style={{ fontSize: '0.8rem', padding: '10px 24px' }}
                      >
                        Buy
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Remaining releases */}
          <div style={{ borderTop: '1px solid rgba(255,26,26,0.3)' }}>
            {releases.slice(1).map((release, idx) => (
              <div
                key={release.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '20px',
                  alignItems: 'center',
                  padding: '20px 0',
                  borderBottom: '1px solid rgba(255,26,26,0.2)',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,26,26,0.04)';
                  setHovered(release.id);
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  setHovered(null);
                }}
              >
                <span
                  style={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.7rem',
                    color: '#444444',
                    width: '28px',
                    textAlign: 'right',
                  }}
                >
                  {String(idx + 2).padStart(2, '0')}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div
                    style={{
                      width: '56px',
                      height: '56px',
                      flexShrink: 0,
                      overflow: 'hidden',
                      border: '1px solid rgba(255,26,26,0.4)',
                    }}
                  >
                    <AppImage
                      src={release.image}
                      alt={release.alt}
                      width={56}
                      height={56}
                      className="w-full h-full object-cover"
                      style={{
                        filter: hovered === release.id ? 'grayscale(0%)' : 'grayscale(80%)',
                        transition: 'filter 0.4s ease',
                      }}
                    />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        fontSize: '1rem',
                        color: '#ffffff',
                        lineHeight: '1.2',
                      }}
                    >
                      {release.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.7rem',
                        color: '#cccccc',
                        letterSpacing: '0.08em',
                        marginTop: '4px',
                      }}
                    >
                      {release.artist}
                    </p>
                    <p
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.65rem',
                        color: '#666666',
                        letterSpacing: '0.08em',
                        marginTop: '2px',
                      }}
                    >
                      {release.genre}
                    </p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
                  <div style={{ textAlign: 'right' }}>
                    <span
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.65rem',
                        color: '#ff1a1a',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        display: 'block',
                      }}
                    >
                      {release.type}
                    </span>
                    <span
                      style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.65rem',
                        color: '#666666',
                      }}
                    >
                      {release.year}
                    </span>
                  </div>
                  <a
                    href={release.listenUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-outline"
                    style={{ fontSize: '0.7rem', padding: '6px 16px' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {release.type === 'Video' ? 'Watch' : 'Listen'}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
